import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { App } from 'antd';

import {
	dismissNotification,
	getNotifications,
	markAllNotificationsRead,
	markNotificationRead,
	markNotificationUnread,
	respondToNotification,
} from '../services/notifications';
import { apiErrorMessage } from '../utils/apiErrors';
import { normalizeId } from '../utils/ids';
import { useCurrentUser } from './CurrentUserContext';
import { getSessionUserId, useOnSessionChange } from '../utils/session';

/**
 * The signed-in user's notification inbox — the app's FIRST server-state poll
 * (only a cosmetic relative-time ticker existed before this). Modeled on
 * CardDefinitionsContext: one fetch latch, a memoized value, a throwing hook
 * plus a null-tolerant one for components that must still render standalone.
 *
 * Two rules keep the poll from becoming a nuisance:
 *
 * 1. It runs ONLY while a user is signed in. Polling signed-out would fight the
 *    api client's 401 interceptor, which redirects the whole window to /login —
 *    a background timer must never be able to bounce someone off a page they
 *    are reading. `useCurrentUser()` is the gate: it holds a user exactly when
 *    the token is valid and the profile fetch succeeded.
 * 2. Window focus refreshes too. A minute is a long time to look at a stale
 *    badge after coming back to the tab, and focus is free.
 *
 * Every write — read, unread, dismiss, answer — is optimistic with rollback,
 * like every other one-field mutation in the app: the dot disappears on click,
 * and only a failure puts it back (with a message).
 *
 * The list is PAGED and FILTERED: `filter` is the drawer's Open · All switch,
 * `loadMore()` walks older rows through the server's cursor, and the first
 * page is re-read on every poll. A refresh is therefore authoritative about
 * the window it just read and silent about everything beneath it — an older
 * page someone has already loaded keeps its place rather than vanishing out
 * from under the reader mid-scroll.
 */
const NotificationsContext = createContext( null );

// One place, per the plan: the bell, the drawer, and any future consumer all
// inherit this cadence.
export const NOTIFICATIONS_POLL_MS = 60000;

// One page of the inbox. Generous, because the first page is nearly always
// the whole product surface; "Load older" exists for the inboxes where it is
// not, and the unread COUNT comes from the header regardless.
const PAGE_SIZE = 50;

const idOf = row => normalizeId( row?._id );

const timeOf = row => {
	const parsed = Date.parse( row?.createdAt || '' );
	return Number.isFinite( parsed ) ? parsed : 0;
};

/**
 * A freshly-read first page over the list we hold.
 *
 * The page is the truth about its own window — a row that was resolved, read
 * elsewhere, or filtered out is GONE from it and must go from the list too —
 * and it says nothing at all about the older pages beneath it, which keep
 * their place. Without that split a poll would either yank away rows somebody
 * is reading or keep stale ones forever, and both are worse than a list that
 * is exactly as fresh as the request that filled it.
 */
const mergeFirstPage = ( fresh, previous ) => {
	if ( ! previous.length ) return fresh;
	const seen = new Set( fresh.map( idOf ) );
	const oldest = fresh.length ? timeOf( fresh[ fresh.length - 1 ] ) : 0;
	const tail = previous.filter( row => ! seen.has( idOf( row ) ) && timeOf( row ) < oldest );
	return [ ...fresh, ...tail ];
};

/** Older rows onto the end, never twice. */
const appendRows = ( previous, older ) => {
	const seen = new Set( previous.map( idOf ) );
	return [ ...previous, ...older.filter( row => ! seen.has( idOf( row ) ) ) ];
};

export function NotificationsProvider( { children } ) {
	const [ notifications, setNotifications ] = useState( [] );
	const [ unreadCount, setUnreadCount ] = useState( 0 );
	const [ openCount, setOpenCount ] = useState( 0 );
	// 'open' is where the inbox opens: a notification is either awaiting
	// something from you or it is history, and history is the other tab.
	const [ filter, setFilter ] = useState( 'open' );
	const [ cursor, setCursor ] = useState( null );
	const [ hasMore, setHasMore ] = useState( false );
	const [ settled, setSettled ] = useState( false );
	const [ fetching, setFetching ] = useState( false );
	const [ loadingMore, setLoadingMore ] = useState( false );
	// The last read failed. Nothing else changes (the last good list stays), but
	// the inbox drawer can say "unable to load" instead of "all caught up".
	const [ loadFailed, setLoadFailed ] = useState( false );
	const inFlightRef = useRef( false );

	// Another account (or none) on this tab: the inbox empties at once, and a
	// poll still in flight for the last account is dropped when it lands
	// (below) — `signedIn` alone flips only once the new account's record
	// has loaded, and a poll answering in between merged the last account's
	// rows into the next one's inbox.
	useOnSessionChange( () => {
		inFlightRef.current = false;
		setNotifications( [] );
		setUnreadCount( 0 );
		setOpenCount( 0 );
		setCursor( null );
		setHasMore( false );
		setSettled( false );
	} );

	const { user } = useCurrentUser();
	const signedIn = !! user;
	const currentUserId = normalizeId( user?._id );
	const { message } = App.useApp();

	const load = useCallback( async ( { replace = false } = {} ) => {
		// Dedupe concurrent triggers only (a focus landing on a tick): the poll
		// is meant to run again, the same request twice is not.
		if ( inFlightRef.current ) return;
		inFlightRef.current = true;
		setFetching( true );
		const startedFor = getSessionUserId();
		try {
			const page = await getNotifications( { state: filter, limit: PAGE_SIZE } );
			if ( getSessionUserId() !== startedFor ) return;
			setNotifications( previous => ( replace ? page.rows : mergeFirstPage( page.rows, previous ) ) );
			setUnreadCount( page.unreadCount );
			setOpenCount( page.openCount );
			setCursor( page.nextCursor );
			setHasMore( page.hasMore );
			setLoadFailed( false );
		}
		catch {
			setLoadFailed( true );
			// The inbox is an enhancement on every screen it appears on; a failed
			// poll leaves the last good list in place rather than blanking a
			// header badge over a transient error.
		}
		finally {
			inFlightRef.current = false;
			setFetching( false );
			setSettled( true );
		}
	}, [ filter ] );

	const refresh = useCallback( () => load(), [ load ] );

	useEffect( () => {
		if ( ! signedIn ) {
			// Signing out clears the inbox for the next login — a second user on
			// the same browser must not inherit rows.
			setNotifications( [] );
			setUnreadCount( 0 );
			setOpenCount( 0 );
			setCursor( null );
			setHasMore( false );
			setSettled( false );
			return undefined;
		}

		// `load` changes identity with the filter, so switching tabs re-runs
		// this effect: page one of the new filter, from scratch, and a poll that
		// from then on refreshes the tab you are actually looking at.
		load( { replace: true } );
		const timer = setInterval( () => { load(); }, NOTIFICATIONS_POLL_MS );
		const onFocus = () => { load(); };
		window.addEventListener( 'focus', onFocus );

		return () => {
			clearInterval( timer );
			window.removeEventListener( 'focus', onFocus );
		};
	}, [ signedIn, load ] );

	/**
	 * The next page of older rows. The cursor is the server's (or, against a
	 * build that does not send one yet, the last row's id) and the list only
	 * ever hands back what it was given.
	 */
	const loadMore = useCallback( async () => {
		if ( ! cursor || ! hasMore || loadingMore ) return;
		setLoadingMore( true );
		const startedFor = getSessionUserId();
		try {
			const page = await getNotifications( { state: filter, limit: PAGE_SIZE, cursor } );
			if ( getSessionUserId() !== startedFor ) return;
			let added = 0;
			setNotifications( previous => {
				const next = appendRows( previous, page.rows );
				added = next.length - previous.length;
				return next;
			} );
			setUnreadCount( page.unreadCount );
			setCursor( page.nextCursor );
			// A page that added nothing new is the end of the list, whatever the
			// headers claim: a server that ignored the cursor would otherwise
			// hand back page one forever behind a button that never finishes.
			setHasMore( page.hasMore && added > 0 );
		}
		catch {
			message.error( 'Could not load older notifications. Please try again.' );
		}
		finally {
			setLoadingMore( false );
		}
	}, [ cursor, hasMore, loadingMore, filter, message ] );

	// One row, replaced in place — every optimistic write and its rollback.
	const replaceRow = useCallback( ( id, next ) => {
		setNotifications( previous => previous.map( row => ( idOf( row ) === id ? next : row ) ) );
	}, [] );

	const markRead = useCallback( async ( id ) => {
		const target = normalizeId( id );
		const row = notifications.find( n => idOf( n ) === target );
		// Already-read rows are a no-op rather than a wasted request: every row
		// click marks read, and most clicks land on rows already read.
		if ( ! target || ! row || row.readAt ) return;

		const readAt = new Date().toISOString();
		replaceRow( target, { ...row, readAt } );
		setUnreadCount( count => Math.max( count - 1, 0 ) );

		try {
			await markNotificationRead( target );
		}
		catch ( err ) {
			replaceRow( target, row );
			setUnreadCount( count => count + 1 );
			message.error( 'Could not mark that notification read. Please try again.' );
		}
	}, [ notifications, replaceRow, message ] );

	/** The other direction: a row you are not finished with keeps its dot. */
	const markUnread = useCallback( async ( id ) => {
		const target = normalizeId( id );
		const row = notifications.find( n => idOf( n ) === target );
		if ( ! target || ! row || ! row.readAt ) return;

		replaceRow( target, { ...row, readAt: null } );
		setUnreadCount( count => count + 1 );

		try {
			await markNotificationUnread( target );
		}
		catch ( err ) {
			replaceRow( target, row );
			setUnreadCount( count => Math.max( count - 1, 0 ) );
			message.error( 'Could not mark that notification unread. Please try again.' );
		}
	}, [ notifications, replaceRow, message ] );

	const markAllRead = useCallback( async () => {
		const previousRows = notifications;
		const previousCount = unreadCount;
		if ( ! previousCount && ! previousRows.some( row => ! row?.readAt ) ) return;

		const readAt = new Date().toISOString();
		setNotifications( previous => previous.map( row => ( row?.readAt ? row : { ...row, readAt } ) ) );
		setUnreadCount( 0 );

		try {
			await markAllNotificationsRead();
		}
		catch ( err ) {
			setNotifications( previousRows );
			setUnreadCount( previousCount );
			message.error( 'Could not mark your notifications read. Please try again.' );
		}
	}, [ notifications, unreadCount, message ] );

	/** Take one row out of this inbox. The alert behind it is untouched. */
	const dismiss = useCallback( async ( id ) => {
		const target = normalizeId( id );
		const index = notifications.findIndex( n => idOf( n ) === target );
		if ( ! target || index < 0 ) return;
		const row = notifications[ index ];

		setNotifications( previous => previous.filter( n => idOf( n ) !== target ) );
		if ( ! row.readAt ) setUnreadCount( count => Math.max( count - 1, 0 ) );

		try {
			await dismissNotification( target );
		}
		catch ( err ) {
			// Back where it was, not on the end: a row that reappears somewhere
			// else reads as a different notification.
			setNotifications( previous => {
				const restored = [ ...previous ];
				restored.splice( Math.min( index, restored.length ), 0, row );
				return restored;
			} );
			if ( ! row.readAt ) setUnreadCount( count => count + 1 );
			message.error( apiErrorMessage( err, 'Could not dismiss that notification. Please try again.' ) );
		}
	}, [ notifications, message ] );

	/**
	 * Answer an open alert. The server hands the whole updated row back, so what
	 * the answer did to the ladder and to the alert's status is the server's
	 * word and not a guess made here; the optimistic write exists only so the
	 * buttons give way to the resolution line in the same frame as the click.
	 */
	const respond = useCallback( async ( id, responseKey, { text } = {} ) => {
		const target = normalizeId( id );
		const row = notifications.find( n => idOf( n ) === target );
		if ( ! target || ! row || ! responseKey ) return;

		const at = new Date().toISOString();
		replaceRow( target, {
			...row,
			respondedBy: { userId: currentUserId, responseKey, at },
			// Answering a notification has read it.
			readAt: row.readAt || at,
		} );
		if ( ! row.readAt ) setUnreadCount( count => Math.max( count - 1, 0 ) );

		try {
			const saved = await respondToNotification( target, responseKey, { text } );
			if ( saved && saved._id ) replaceRow( target, saved );
		}
		catch ( err ) {
			replaceRow( target, row );
			if ( ! row.readAt ) setUnreadCount( count => count + 1 );
			// ⚠ THE SERVER'S SENTENCE WHERE THERE IS ONE — the repo's idiom at
			// every other write, and it matters more here than most. A refused
			// answer is usually not a glitch worth retrying: the API writes
			// "Your access to the record this is about has changed, so the
			// answer was not recorded." (403) and three distinct 400s, each of
			// which says something "please try again" does not — the row keeps
			// its live buttons across refreshes, so a reader told to retry will,
			// forever, and each attempt is logged as a suppressed ack. The
			// generic line stays for the case it is actually true of: a
			// transport failure that carried no sentence at all.
			message.error( apiErrorMessage( err, 'Could not send that answer. Please try again.' ) );
		}
	}, [ notifications, replaceRow, currentUserId, message ] );

	const value = useMemo( () => ( {
		notifications,
		unreadCount,
		openCount,
		filter,
		setFilter,
		hasMore,
		loadingMore,
		loadMore,
		loading: fetching || ( signedIn && ! settled ),
		loadFailed,
		currentUserId,
		refresh,
		markRead,
		markUnread,
		markAllRead,
		dismiss,
		respond,
	} ), [
		notifications, unreadCount, openCount, filter, hasMore, loadingMore, loadMore,
		fetching, signedIn, settled, loadFailed, currentUserId, refresh, markRead, markUnread,
		markAllRead, dismiss, respond,
	] );

	return (
		<NotificationsContext.Provider value={value}>
			{children}
		</NotificationsContext.Provider>
	);
}

export function useNotifications() {
	const ctx = useContext( NotificationsContext );
	if ( ! ctx ) {
		throw new Error( 'useNotifications must be used within a NotificationsProvider' );
	}
	return ctx;
}

/**
 * Null instead of throwing. The header Layout and the drawer both render in
 * tests (and in the public share/landing pages) without the provider — an
 * absent inbox degrades to "no unread", never to a crash.
 */
export function useOptionalNotifications() {
	return useContext( NotificationsContext );
}
